import apiClient from './apiClient';

/** Public base URL for Azure Blob Storage releases (configured via env). */
const RELEASE_BASE_URL = (process.env.REACT_APP_WINDOWS_RELEASE_BASE_URL || '').replace(/\/$/, '');

export const WINDOWS_ARCHITECTURES = ['x64', 'arm64'];

const isArchitecture = (arch) => WINDOWS_ARCHITECTURES.includes(arch);
const isHttpsUrl = (value) => {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password;
  } catch {
    return false;
  }
};
const hasArchitecturePair = (value) =>
  Array.isArray(value) && value.length === 2 &&
  WINDOWS_ARCHITECTURES.every((arch) => value.includes(arch));

/**
 * Validate before exposing any download. Legacy releases identify their single
 * installer by top-level arch; v2 identifies each installer explicitly.
 */
export function validateRelease(release) {
  const invalid = () => { throw new Error('Invalid Qt release manifest'); };
  if (!release || typeof release.version !== 'string' || !release.version.trim() ||
      !Array.isArray(release.installers)) invalid();

  const v2 = release.schemaVersion === 2;
  if (v2) {
    if (!hasArchitecturePair(release.architectures) || release.installers.length !== 2 ||
        Object.prototype.hasOwnProperty.call(release, 'arch')) invalid();
  } else if ((release.schemaVersion !== undefined && release.schemaVersion !== 1) ||
      !isArchitecture(release.arch) || release.installers.length !== 1 ||
      (release.architectures !== undefined &&
        (!Array.isArray(release.architectures) || release.architectures.length !== 1 ||
          release.architectures[0] !== release.arch))) invalid();

  const seen = new Set();
  const seenUrls = new Set();
  for (const installer of release.installers) {
    if (!installer || typeof installer.file !== 'string' ||
        !installer.file.toLowerCase().endsWith('.exe') || !isHttpsUrl(installer.url)) invalid();
    const downloadUrl = new URL(installer.url);
    downloadUrl.hash = '';
    if (seenUrls.has(downloadUrl.href)) invalid();
    seenUrls.add(downloadUrl.href);
    const arch = v2 ? installer.arch : release.arch;
    if (!isArchitecture(arch) || seen.has(arch) ||
        (!v2 && installer.arch !== undefined && installer.arch !== arch)) invalid();
    if ((v2 || installer.sha256 !== undefined) &&
        (typeof installer.sha256 !== 'string' || !/^[a-f0-9]{64}$/i.test(installer.sha256))) invalid();
    if ((v2 || installer.fileSize !== undefined) &&
        (!Number.isSafeInteger(installer.fileSize) || installer.fileSize <= 0)) invalid();
    seen.add(arch);
  }
  return release;
}

/**
 * Fetches the latest Windows client release information.
 * Step 1: GET ${RELEASE_BASE_URL}/qt-latest.json (no-cache, points to current Qt version)
 * Step 2: GET latest.releaseManifestUrl           (qt-release.json with full metadata)
 * Both requests use plain fetch — no auth required (public blob).
 */
export async function fetchLatestRelease() {
  if (!RELEASE_BASE_URL) {
    throw new Error('REACT_APP_WINDOWS_RELEASE_BASE_URL is not configured');
  }

  const latestRes = await fetch(`${RELEASE_BASE_URL}/qt-latest.json`, { cache: 'no-store' });
  if (!latestRes.ok) throw new Error(`qt-latest.json: HTTP ${latestRes.status}`);
  const latest = await latestRes.json();

  const releaseManifestUrl = latest?.releaseManifestUrl;
  if (!releaseManifestUrl) throw new Error('qt-latest.json is missing releaseManifestUrl');
  if (!isHttpsUrl(releaseManifestUrl) ||
      (latest.schemaVersion !== undefined && ![1, 2].includes(latest.schemaVersion)) ||
      (latest.schemaVersion === 2 && latest.architectures !== undefined &&
        !hasArchitecturePair(latest.architectures))) {
    throw new Error('Invalid Qt latest manifest');
  }

  const manifestRes = await fetch(releaseManifestUrl);
  if (!manifestRes.ok) throw new Error(`release manifest: HTTP ${manifestRes.status}`);
  const release = validateRelease(await manifestRes.json());
  if (latest.version !== release.version ||
      (latest.schemaVersion === 2 && release.schemaVersion !== 2)) {
    throw new Error('Invalid Qt release manifest: latest/release mismatch');
  }
  return release;
}

/**
 * Resolve only by declared architecture, never array order or artifact names.
 * Invalid releases expose no download, including when read from a query cache.
 */
export function pickExeInstaller(release, arch) {
  if (!isArchitecture(arch)) return null;
  try {
    validateRelease(release);
    return release.schemaVersion === 2
      ? release.installers.find((installer) => installer.arch === arch) ?? null
      : release.arch === arch ? release.installers[0] : null;
  } catch {
    return null;
  }
}

/**
 * POST /feature-requests — authenticated via apiClient interceptor.
 * The backend derives userId from the bearer token; the frontend does NOT send identity.
 * @param {{ title: string, description: string }} payload
 * @returns {Promise<{ id: string, status: string, createdAt: string }>}
 */
export async function submitFeatureRequest({ title, description }) {
  const { data } = await apiClient.post('/feature-requests', { title, description });
  return data;
}
